/**
 * Error Reporting Utility
 *
 * Centralized error logging and reporting for the application.
 * In development, logs to console. In production, sends errors to Sentry.
 */

import type { ErrorInfo } from "react";
import * as Sentry from "@sentry/react";

/**
 * Error context information
 */
export interface ErrorContext {
  /** Component name or location where the error occurred */
  componentName?: string;
  /** Additional context about what was happening */
  action?: string;
  /** Any additional metadata */
  metadata?: Record<string, unknown>;
}

/**
 * Report an error to the logging/monitoring system
 *
 * @param error - The error that occurred
 * @param errorInfo - React error info (component stack)
 * @param context - Additional context about where/why the error occurred
 */
export function reportError(
  error: Error,
  errorInfo?: ErrorInfo,
  context?: ErrorContext,
): void {
  // Always log to console in development
  if (import.meta.env.DEV) {
    console.group("[Error Report]");
    console.error("Error:", error);
    if (errorInfo?.componentStack) {
      console.error("Component Stack:", errorInfo.componentStack);
    }
    if (context) {
      console.error("Context:", context);
    }
    console.groupEnd();
  }

  // In production, send to Sentry
  if (import.meta.env.PROD) {
    Sentry.captureException(error, {
      contexts: {
        react: { componentStack: errorInfo?.componentStack },
      },
      tags: {
        component: context?.componentName,
        action: context?.action,
      },
    });
  }
}

/**
 * Create an error handler function for ErrorBoundary onError prop
 *
 * @param componentName - Name of the component/feature being wrapped
 * @returns Error handler function compatible with ErrorBoundary
 */
export function createErrorHandler(componentName: string) {
  return (error: Error, errorInfo: ErrorInfo): void => {
    reportError(error, errorInfo, { componentName });
  };
}

/**
 * Report a warning (non-critical error)
 *
 * @param message - Warning message
 * @param context - Additional context
 */
export function reportWarning(message: string, context?: ErrorContext): void {
  if (import.meta.env.DEV) {
    console.warn("[Warning]", message, context);
  }

  if (import.meta.env.PROD) {
    Sentry.captureMessage(message, {
      level: "warning",
      tags: {
        component: context?.componentName,
        action: context?.action,
      },
    });
  }
}
